import * as React from "react";
import IconButton from "@mui/material/IconButton";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import cloud from "../../utils/images/cloud.jpeg";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ImageProps } from "../../interfaces/interfaces";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export const TitlebarImageList = ({
  itemData,
  index,
  onChange,
  handleDeleteImage,
}: {
  itemData: ImageProps[];
  index: number;
  onChange: (indexLine: number, indexDetail: number, value: ImageProps) => void;
  handleDeleteImage: (indexLine: number, indexDetail: number) => void;
}) => {
  const handleUploadImage = (
    event: React.ChangeEvent<HTMLInputElement>,
    idx: number,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        if (reader.result && typeof reader.result === "string") {
          onChange(index, idx, {
            size: itemData[idx]?.size || 1,
            scale: itemData[idx]?.scale || 1,
            rotate: 0,
            image: reader.result,
          });
        }
      };
    }
  };

  const handleRotateImage = (item: ImageProps, indexImage: number) => {
    const nextRotation =
      item.rotate === 0 ? 90
      : item.rotate === 90 ? 180
      : item.rotate === 180 ? 270
      : 0;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    if (ctx && item.image) {
      img.onload = () => {
        canvas.width = img.height;
        canvas.height = img.width;
        ctx.rotate(Math.PI / 2);
        ctx.drawImage(img, 0, -canvas.width);
        onChange(index, indexImage, {
          ...item,
          rotate: nextRotation,
          image: canvas.toDataURL("image/png"),
        });
      };
      img.src = item.image;
    }
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 1 }}>
      {itemData.map((item, idx) => (
        <Paper
          key={idx}
          variant="outlined"
          sx={{ width: 210, borderRadius: 1, overflow: "hidden", flexShrink: 0 }}
        >
          {/* Image preview */}
          <Box
            sx={{
              position: "relative",
              height: 130,
              bgcolor: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={item.image || cloud}
              alt="evidence"
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />

            {/* Action buttons — bottom-right overlay */}
            <Box
              sx={{
                position: "absolute",
                bottom: 4,
                right: 4,
                display: "flex",
                gap: 0.25,
                bgcolor: "rgba(0,0,0,0.45)",
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
              }}
            >
              <Tooltip title="Rotate 90°">
                <IconButton
                  size="small"
                  onClick={() => handleRotateImage(item, idx)}
                  sx={{ color: "#fff", p: 0.4 }}
                >
                  <RotateRightIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Upload image">
                <IconButton component="label" size="small" sx={{ color: "#fff", p: 0.4 }}>
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleUploadImage(e, idx)}
                  />
                  <FileUploadIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove photo">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteImage(index, idx)}
                  sx={{ color: "#ffaaaa", p: 0.4 }}
                >
                  <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Shape and Scale controls — stacked for readability */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, p: 0.75, bgcolor: "#fafafa" }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Shape</InputLabel>
              <Select
                value={item.size || 1}
                label="Shape"
                onChange={(e) =>
                  onChange(index, idx, {
                    ...item,
                    size: parseInt(e.target.value.toString()),
                  })
                }
              >
                <MenuItem value={1}>Landscape</MenuItem>
                <MenuItem value={2}>Wide landscape</MenuItem>
                <MenuItem value={3}>Square</MenuItem>
                <MenuItem value={4}>Portrait</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Scale</InputLabel>
              <Select
                value={item.scale || 1}
                label="Scale"
                onChange={(e) =>
                  onChange(index, idx, {
                    ...item,
                    scale: parseFloat(e.target.value.toString()),
                  })
                }
              >
                <MenuItem value={0.5}>50%</MenuItem>
                <MenuItem value={0.6}>60%</MenuItem>
                <MenuItem value={0.7}>70%</MenuItem>
                <MenuItem value={0.8}>80%</MenuItem>
                <MenuItem value={0.9}>90%</MenuItem>
                <MenuItem value={1}>100% (default)</MenuItem>
                <MenuItem value={1.1}>110%</MenuItem>
                <MenuItem value={1.2}>120%</MenuItem>
                <MenuItem value={1.3}>130%</MenuItem>
                <MenuItem value={1.4}>140%</MenuItem>
                <MenuItem value={1.5}>150%</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      ))}
    </Box>
  );
};
